import { useState, useMemo } from 'react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Badge } from '@/components/ui/Badge'
import { Spinner } from '@/components/ui/Spinner'
import { EmptyState } from '@/components/ui/EmptyState'
import { useProspectsList } from '@/hooks/useProspection'
import { useProspectCalls, useCreateCall, useUpcomingCallbacks, useDeleteCall } from '@/hooks/useProspectCalls'
import { useDebounce } from '@/hooks/useDebounce'
import {
  STATUT_LABELS,
  STATUT_COLORS,
  CALL_OUTCOME_LABELS,
  CALL_OUTCOME_COLORS,
} from '@/types/prospection'
import type { Prospect, CallOutcome } from '@/types/prospection'
import {
  Search,
  Phone,
  PhoneCall,
  PhoneOff,
  Calendar,
  Clock,
  FileText,
  Building2,
  Mail,
  Globe,
  XCircle,
  MapPin,
  Save,
  Trash2,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  User,
  Users,
  Target,
  MessageSquare,
  Shield,
  TrendingUp,
  DollarSign,
  Zap,
  ChevronDown,
  ChevronUp,
  List,
} from 'lucide-react'
import { format, formatDistanceToNow, isPast } from 'date-fns'
import { fr } from 'date-fns/locale'

// Helper to safely access nested qualification data
function getQualif(prospect: Prospect | null, ...path: string[]): any {
  if (!prospect?.qualification) return null
  let val: any = prospect.qualification
  for (const key of path) {
    if (val == null || typeof val !== 'object') return null
    val = val[key]
  }
  return val
}

export default function CallPanel() {
  const [search, setSearch] = useState('')
  const [selectedProspect, setSelectedProspect] = useState<Prospect | null>(null)
  const [showSidebar, setShowSidebar] = useState(true)
  const debouncedSearch = useDebounce(search, 300)

  // Call form state
  const [callScript, setCallScript] = useState('')
  const [callNotes, setCallNotes] = useState('')
  const [outcome, setOutcome] = useState<CallOutcome | ''>('')
  const [nextAppointment, setNextAppointment] = useState('')
  const [durationMinutes, setDurationMinutes] = useState('')
  const [contactName, setContactName] = useState('')

  // Workflow confirmation email (visible seulement si un RDV est défini)
  const [emailChoice, setEmailChoice] = useState<'existing' | 'custom' | null>(null)
  const [customEmail, setCustomEmail] = useState('')

  // Collapsible sections
  const [showArgumentaire, setShowArgumentaire] = useState(true)
  const [showObjections, setShowObjections] = useState(false)
  const [showProblemes, setShowProblemes] = useState(false)
  const [showConcurrence, setShowConcurrence] = useState(false)
  const [showHistory, setShowHistory] = useState(true)

  // Queries
  const { data: prospectsData, isLoading: prospectsLoading } = useProspectsList({
    search: debouncedSearch,
    limit: 15,
    sortBy: 'score_priorite',
    sortOrder: 'desc',
  })

  const { data: calls, isLoading: callsLoading } = useProspectCalls(selectedProspect?.id)
  const { data: upcomingCallbacks } = useUpcomingCallbacks()
  const createCall = useCreateCall()
  const deleteCall = useDeleteCall()

  const handleSelectProspect = (prospect: Prospect) => {
    setSelectedProspect(prospect)
    setShowSidebar(false)
    setContactName(prospect.contact_name || prospect.dirigeant || '')
    setCallScript('')
    setCallNotes('')
    setOutcome('')
    setNextAppointment('')
    setDurationMinutes('')
    setEmailChoice(null)
    setCustomEmail('')
  }

  const effectiveEmail =
    emailChoice === 'existing'
      ? selectedProspect?.email || ''
      : customEmail.trim()

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(effectiveEmail)

  // Si un RDV est défini, on exige un email valide pour enregistrer.
  const needsEmail = nextAppointment.length > 0
  const emailBlocksSave = needsEmail && !isEmailValid

  const handleSaveCall = async () => {
    if (!selectedProspect) return

    await createCall.mutateAsync({
      prospect_id: selectedProspect.id,
      call_script: callScript || null,
      notes: callNotes || null,
      outcome: (outcome as CallOutcome) || null,
      next_appointment: nextAppointment ? new Date(nextAppointment).toISOString() : null,
      duration_minutes: durationMinutes ? parseInt(durationMinutes, 10) : null,
      contact_email: needsEmail ? effectiveEmail : null,
    })

    setCallScript('')
    setCallNotes('')
    setOutcome('')
    setNextAppointment('')
    setDurationMinutes('')
    setEmailChoice(null)
    setCustomEmail('')
  }

  const handleDeleteCall = async (callId: string) => {
    if (!selectedProspect || !confirm('Supprimer cet appel ?')) return
    await deleteCall.mutateAsync({ id: callId, prospectId: selectedProspect.id })
  }

  const lastCallScript = useMemo(() => {
    if (!calls?.length) return null
    return calls.find(c => c.call_script)?.call_script || null
  }, [calls])

  // Qualification data shortcuts
  const segment = getQualif(selectedProspect, 'segment')
  const scoreQualif = getQualif(selectedProspect, 'score_qualification')
  const argumentaire = getQualif(selectedProspect, 'argumentaire')
  const strategie = getQualif(selectedProspect, 'strategie_approche')
  const authority = getQualif(selectedProspect, 'authority')
  const money = getQualif(selectedProspect, 'money')
  const prioritization = getQualif(selectedProspect, 'prioritization')
  const challenges = getQualif(selectedProspect, 'challenges')
  const objections = strategie?.objections as { objection: string; reponse: string }[] | null
  const problemes = challenges?.problemes_digitaux as { axe: string; present: boolean; description: string }[] | null
  const concurrents = challenges?.analyse_concurrentielle as { nom: string; site_web: string; observation: string }[] | null
  const axes = challenges?.axes_amelioration as { axe: string; priorite: string; offre_lsw: string; estimation: string; description: string }[] | null

  const segmentColor = segment === 'HOT' ? 'red' : segment === 'WARM' ? 'gold' : 'blue'

  return (
    <div className="space-y-4">
      {/* Upcoming callbacks banner */}
      {upcomingCallbacks && upcomingCallbacks.length > 0 && (
        <Card padding="sm">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-4 h-4 text-gold-600" />
            <h3 className="text-sm font-semibold text-brown-900">
              Prochains rappels ({upcomingCallbacks.length})
            </h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {upcomingCallbacks.slice(0, 5).map((cb) => {
              const prospectName = cb.prospects?.nom_commercial || cb.prospects?.raison_sociale || 'Inconnu'
              const isUrgent = cb.next_appointment && isPast(new Date(cb.next_appointment))
              return (
                <button
                  key={cb.id}
                  onClick={() => {
                    if (cb.prospects) handleSelectProspect(cb.prospects as any)
                  }}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    isUrgent
                      ? 'bg-red-50 text-red-700 hover:bg-red-100'
                      : 'bg-gold-50 text-gold-700 hover:bg-gold-100'
                  }`}
                >
                  {isUrgent && <AlertCircle className="w-3 h-3" />}
                  <span>{prospectName}</span>
                  <span className="text-xs opacity-75">
                    {cb.next_appointment && formatDistanceToNow(new Date(cb.next_appointment), { locale: fr, addSuffix: true })}
                  </span>
                </button>
              )
            })}
          </div>
        </Card>
      )}

      <div className="flex gap-6">
        {/* Left: Prospect selector (collapsible) */}
        {showSidebar && (
          <div className="w-80 flex-shrink-0 space-y-4">
            <Card>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-brown-900 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-mint-600" />
                  Prospects
                </h3>
                {selectedProspect && (
                  <button
                    onClick={() => setShowSidebar(false)}
                    className="p-1 text-brown-400 hover:text-brown-600 transition-colors"
                    title="Masquer la liste"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                )}
              </div>
              <Input
                type="search"
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftIcon={<Search className="w-4 h-4" />}
              />
              <div className="mt-3 max-h-[calc(100vh-320px)] overflow-y-auto space-y-1">
                {prospectsLoading ? (
                  <div className="flex justify-center py-6">
                    <Spinner size="sm" />
                  </div>
                ) : prospectsData?.prospects?.length === 0 ? (
                  <p className="text-xs text-brown-400 text-center py-4">
                    Aucun prospect trouve
                  </p>
                ) : (
                  prospectsData?.prospects.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => handleSelectProspect(p)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg transition-colors ${
                        selectedProspect?.id === p.id
                          ? 'bg-mint-50 border border-mint-200'
                          : 'hover:bg-beige-50 border border-transparent'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-brown-900 truncate">
                            {p.nom_commercial || p.raison_sociale || '-'}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {p.telephone && (
                              <span className="text-xs text-brown-500 flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {p.telephone}
                              </span>
                            )}
                          </div>
                        </div>
                        <Badge variant={STATUT_COLORS[p.statut || 'lead']} size="sm">
                          {STATUT_LABELS[p.statut || 'lead']}
                        </Badge>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Right: Call panel (full width when sidebar hidden) */}
        <div className="flex-1 min-w-0 space-y-4">
          {!selectedProspect ? (
            <Card padding="lg">
              <EmptyState
                icon={<PhoneCall className="w-12 h-12" />}
                title="Selectionnez un prospect"
                description="Choisissez un prospect dans la liste pour demarrer un appel"
              />
            </Card>
          ) : (
            <>
              {/* Prospect info header + show sidebar button */}
              <Card padding="sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    {!showSidebar && (
                      <button
                        onClick={() => setShowSidebar(true)}
                        className="p-2 bg-beige-100 text-brown-600 rounded-lg hover:bg-beige-200 transition-colors flex-shrink-0"
                        title="Afficher la liste"
                      >
                        <List className="w-4 h-4" />
                      </button>
                    )}
                    <div className="p-2 bg-mint-50 rounded-lg flex-shrink-0">
                      <Building2 className="w-5 h-5 text-mint-600" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-brown-900">
                          {selectedProspect.nom_commercial || selectedProspect.raison_sociale}
                        </h3>
                        {segment && (
                          <Badge variant={segmentColor as any} size="sm">
                            {segment}
                          </Badge>
                        )}
                        {scoreQualif && (
                          <span className="text-xs font-medium text-brown-500">
                            Score: {typeof scoreQualif === 'number' ? scoreQualif : scoreQualif}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-1">
                        {selectedProspect.telephone && (
                          <a
                            href={`tel:${selectedProspect.telephone}`}
                            className="flex items-center gap-1 text-sm text-mint-600 hover:text-mint-700 font-medium"
                          >
                            <Phone className="w-3.5 h-3.5" />
                            {selectedProspect.telephone}
                          </a>
                        )}
                        {selectedProspect.email && (
                          <a
                            href={`mailto:${selectedProspect.email}`}
                            className="flex items-center gap-1 text-sm text-brown-500 hover:text-brown-700"
                          >
                            <Mail className="w-3.5 h-3.5" />
                            {selectedProspect.email}
                          </a>
                        )}
                        {selectedProspect.ville && (
                          <span className="flex items-center gap-1 text-sm text-brown-400">
                            <MapPin className="w-3.5 h-3.5" />
                            {selectedProspect.ville}
                          </span>
                        )}
                        {selectedProspect.secteur && (
                          <span className="text-xs text-brown-400">{selectedProspect.secteur}</span>
                        )}
                        {authority?.nom_decideur && (
                          <span className="flex items-center gap-1 text-xs text-brown-500">
                            <User className="w-3 h-3" />
                            {authority.nom_decideur}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge variant={STATUT_COLORS[selectedProspect.statut || 'lead']}>
                          {STATUT_LABELS[selectedProspect.statut || 'lead']}
                        </Badge>
                        {selectedProspect.a_un_site ? (
                          <Badge variant="mint" size="sm">
                            <Globe className="w-3 h-3 mr-1" /> Site web
                          </Badge>
                        ) : (
                          <Badge variant="red" size="sm">
                            <XCircle className="w-3 h-3 mr-1" /> Pas de site
                          </Badge>
                        )}
                        {selectedProspect.next_appointment && (
                          <Badge variant="gold" size="sm">
                            <Calendar className="w-3 h-3 mr-1" />
                            RDV: {format(new Date(selectedProspect.next_appointment), 'd MMM HH:mm', { locale: fr })}
                          </Badge>
                        )}
                        {selectedProspect.effectifs && (
                          <Badge variant="blue" size="sm">
                            <Users className="w-3 h-3 mr-1" /> {selectedProspect.effectifs}
                          </Badge>
                        )}
                        {money?.estimation_budget_web && (
                          <Badge variant="purple" size="sm">
                            <DollarSign className="w-3 h-3 mr-1" /> {money.estimation_budget_web}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {selectedProspect.telephone && (
                      <a
                        href={`tel:${selectedProspect.telephone}`}
                        className="p-2.5 bg-mint-600 text-white rounded-lg hover:bg-mint-700 transition-colors"
                      >
                        <PhoneCall className="w-5 h-5" />
                      </a>
                    )}
                    <button
                      onClick={() => {
                        setSelectedProspect(null)
                        setShowSidebar(true)
                      }}
                      className="p-2.5 bg-beige-100 text-brown-500 rounded-lg hover:bg-beige-200 transition-colors"
                      title="Changer de prospect"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </Card>

              {/* Scoring bar */}
              {(selectedProspect.score_priorite || selectedProspect.score_besoin || selectedProspect.score_capacite || selectedProspect.score_timing) && (
                <div className="grid grid-cols-4 gap-3">
                  {[
                    { label: 'Priorite', value: selectedProspect.score_priorite, icon: Target },
                    { label: 'Besoin', value: selectedProspect.score_besoin, icon: Zap },
                    { label: 'Capacite', value: selectedProspect.score_capacite, icon: DollarSign },
                    { label: 'Timing', value: selectedProspect.score_timing, icon: Clock },
                  ].map(({ label, value, icon: Icon }) => (
                    <Card padding="sm" key={label}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-3.5 h-3.5 text-mint-600" />
                        <span className="text-xs text-brown-500">{label}</span>
                        <span className="ml-auto text-sm font-bold text-brown-900">
                          {value ?? '-'}
                        </span>
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              {/* Argumentaire + Strategy (collapsible) */}
              {argumentaire && (
                <Card padding="sm">
                  <button
                    onClick={() => setShowArgumentaire(!showArgumentaire)}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <h3 className="text-sm font-semibold text-brown-900 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-mint-600" />
                      Argumentaire & Strategie
                    </h3>
                    {showArgumentaire ? (
                      <ChevronUp className="w-4 h-4 text-brown-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-brown-400" />
                    )}
                  </button>
                  {showArgumentaire && (
                    <div className="mt-3 space-y-3">
                      {/* Message cle */}
                      {strategie?.message_cle && (
                        <div className="p-3 bg-mint-50 border border-mint-200 rounded-lg">
                          <p className="text-xs font-semibold text-mint-800 mb-1">Message cle d'accroche</p>
                          <p className="text-sm text-mint-900">{strategie.message_cle}</p>
                        </div>
                      )}

                      {/* Probleme / Solution / Impact */}
                      {argumentaire.probleme && (
                        <div className="p-3 bg-red-50 border border-red-100 rounded-lg">
                          <p className="text-xs font-semibold text-red-700 mb-1">Probleme identifie</p>
                          <p className="text-sm text-red-900">{argumentaire.probleme}</p>
                        </div>
                      )}
                      {argumentaire.solution && (
                        <div className="p-3 bg-blue-50 border border-blue-100 rounded-lg">
                          <p className="text-xs font-semibold text-blue-700 mb-1">Solution proposee</p>
                          <p className="text-sm text-blue-900">{argumentaire.solution}</p>
                        </div>
                      )}
                      {argumentaire.impact && (
                        <div className="p-3 bg-green-50 border border-green-100 rounded-lg">
                          <p className="text-xs font-semibold text-green-700 mb-1">Impact chiffre</p>
                          <p className="text-sm text-green-900">{argumentaire.impact}</p>
                        </div>
                      )}

                      {/* Approach details */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {strategie?.canal && (
                          <div className="p-2 bg-beige-50 rounded-lg">
                            <p className="text-xs font-medium text-brown-500">Canal</p>
                            <p className="text-sm text-brown-800">{strategie.canal}</p>
                          </div>
                        )}
                        {strategie?.timing && (
                          <div className="p-2 bg-beige-50 rounded-lg">
                            <p className="text-xs font-medium text-brown-500">Timing</p>
                            <p className="text-sm text-brown-800">{strategie.timing}</p>
                          </div>
                        )}
                        {prioritization?.niveau_urgence && (
                          <div className="p-2 bg-beige-50 rounded-lg">
                            <p className="text-xs font-medium text-brown-500">Urgence</p>
                            <p className="text-sm text-brown-800">{prioritization.niveau_urgence}</p>
                          </div>
                        )}
                        {prioritization?.meilleur_moment_contact && (
                          <div className="p-2 bg-beige-50 rounded-lg">
                            <p className="text-xs font-medium text-brown-500">Meilleur moment</p>
                            <p className="text-sm text-brown-800">{prioritization.meilleur_moment_contact}</p>
                          </div>
                        )}
                      </div>

                      {/* Budget recommendation */}
                      {money?.recommandation_tarifaire && (
                        <div className="p-2 bg-purple-50 border border-purple-100 rounded-lg">
                          <p className="text-xs font-semibold text-purple-700 mb-1">Recommandation tarifaire</p>
                          <p className="text-sm text-purple-900">{money.recommandation_tarifaire}</p>
                        </div>
                      )}

                      {/* Axes amelioration */}
                      {axes && axes.length > 0 && (
                        <div>
                          <p className="text-xs font-semibold text-brown-700 mb-2">Offres recommandees</p>
                          <div className="space-y-1">
                            {axes.filter(a => a.priorite === 'P0' || a.priorite === 'P1').map((axe, i) => (
                              <div key={i} className="flex items-start gap-2 text-sm">
                                <Badge variant={axe.priorite === 'P0' ? 'red' : 'gold'} size="sm">
                                  {axe.priorite}
                                </Badge>
                                <span className="text-brown-800 font-medium">{axe.offre_lsw}</span>
                                {axe.estimation && (
                                  <span className="text-brown-500 text-xs ml-auto">{axe.estimation}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              )}

              {/* Objections (collapsible) */}
              {objections && objections.length > 0 && (
                <Card padding="sm">
                  <button
                    onClick={() => setShowObjections(!showObjections)}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <h3 className="text-sm font-semibold text-brown-900 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-gold-600" />
                      Objections & Reponses ({objections.length})
                    </h3>
                    {showObjections ? (
                      <ChevronUp className="w-4 h-4 text-brown-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-brown-400" />
                    )}
                  </button>
                  {showObjections && (
                    <div className="mt-3 space-y-3">
                      {objections.map((obj, i) => (
                        <div key={i} className="p-3 rounded-lg border border-beige-200">
                          <p className="text-xs font-semibold text-red-600 mb-1">
                            "{obj.objection}"
                          </p>
                          <p className="text-sm text-brown-800">{obj.reponse}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              )}

              {/* Problemes digitaux (collapsible) */}
              {problemes && problemes.length > 0 && (
                <Card padding="sm">
                  <button
                    onClick={() => setShowProblemes(!showProblemes)}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <h3 className="text-sm font-semibold text-brown-900 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-500" />
                      Problemes digitaux ({problemes.filter(p => !p.present || p.description?.includes('Aucun')).length}/{problemes.length})
                    </h3>
                    {showProblemes ? (
                      <ChevronUp className="w-4 h-4 text-brown-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-brown-400" />
                    )}
                  </button>
                  {showProblemes && (
                    <div className="mt-3 space-y-1.5">
                      {problemes.map((p, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${p.present && !p.description?.toLowerCase().includes('aucun') ? 'bg-green-500' : 'bg-red-400'}`} />
                          <div>
                            <span className="font-medium text-brown-800">{p.axe}</span>
                            {p.description && (
                              <span className="text-brown-500 text-xs ml-1">— {p.description}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              )}

              {/* Concurrence (collapsible) */}
              {concurrents && concurrents.length > 0 && (
                <Card padding="sm">
                  <button
                    onClick={() => setShowConcurrence(!showConcurrence)}
                    className="w-full flex items-center justify-between text-left"
                  >
                    <h3 className="text-sm font-semibold text-brown-900 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-purple-500" />
                      Concurrence ({concurrents.length})
                    </h3>
                    {showConcurrence ? (
                      <ChevronUp className="w-4 h-4 text-brown-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-brown-400" />
                    )}
                  </button>
                  {showConcurrence && (
                    <div className="mt-3 space-y-2">
                      {concurrents.map((c, i) => (
                        <div key={i} className="p-2 bg-beige-50 rounded-lg text-sm">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-brown-800">{c.nom}</span>
                            <Badge variant={c.site_web && c.site_web !== 'Non' ? 'mint' : 'gray'} size="sm">
                              {c.site_web && c.site_web !== 'Non' ? 'Site web' : 'Pas de site'}
                            </Badge>
                          </div>
                          {c.observation && (
                            <p className="text-xs text-brown-500 mt-0.5">{c.observation}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              )}

              {/* Call form */}
              <Card>
                <h3 className="text-sm font-semibold text-brown-900 mb-4 flex items-center gap-2">
                  <PhoneCall className="w-4 h-4 text-mint-600" />
                  Nouvel appel
                </h3>

                <div className="space-y-4">
                  <Input
                    label="Nom du contact"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder="Personne contactee..."
                    leftIcon={<User className="w-4 h-4" />}
                  />

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-brown-700">
                        Script / Notes d'appel
                      </label>
                      {lastCallScript && !callScript && (
                        <button
                          onClick={() => setCallScript(lastCallScript)}
                          className="text-xs text-mint-600 hover:text-mint-700"
                        >
                          Reprendre le dernier script
                        </button>
                      )}
                    </div>
                    <textarea
                      className="w-full px-4 py-2 border border-beige-300 rounded-lg text-brown-900 text-sm focus:outline-none focus:ring-2 focus:ring-mint-500 focus:border-transparent min-h-[120px] resize-y"
                      value={callScript}
                      onChange={(e) => setCallScript(e.target.value)}
                      placeholder="Script de l'appel, points a aborder..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brown-700 mb-1">
                      Compte-rendu
                    </label>
                    <textarea
                      className="w-full px-4 py-2 border border-beige-300 rounded-lg text-brown-900 text-sm focus:outline-none focus:ring-2 focus:ring-mint-500 focus:border-transparent min-h-[80px] resize-y"
                      value={callNotes}
                      onChange={(e) => setCallNotes(e.target.value)}
                      placeholder="Resume de la conversation, decisions prises..."
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Select
                      label="Resultat"
                      value={outcome}
                      onChange={(e) => setOutcome(e.target.value as CallOutcome | '')}
                    >
                      <option value="">-- Resultat --</option>
                      {Object.entries(CALL_OUTCOME_LABELS).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>

                    <Input
                      label="Duree (min)"
                      type="number"
                      min="0"
                      value={durationMinutes}
                      onChange={(e) => setDurationMinutes(e.target.value)}
                      placeholder="0"
                      leftIcon={<Clock className="w-4 h-4" />}
                    />

                    <Input
                      label="Prochain RDV"
                      type="datetime-local"
                      value={nextAppointment}
                      onChange={(e) => setNextAppointment(e.target.value)}
                      leftIcon={<Calendar className="w-4 h-4" />}
                    />
                  </div>

                  {needsEmail && (
                    <div className="p-4 bg-mint-50 border border-mint-200 rounded-lg space-y-3">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-mint-600" />
                        <p className="text-sm font-semibold text-brown-900">
                          Confirmation de RDV par email
                        </p>
                      </div>

                      {selectedProspect.email && emailChoice !== 'custom' && (
                        <div className="space-y-2">
                          <p className="text-sm text-brown-700">
                            Email en base :{' '}
                            <span className="font-medium">{selectedProspect.email}</span>
                          </p>
                          <p className="text-xs text-brown-500">
                            Est-ce la bonne adresse pour envoyer la confirmation ?
                          </p>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant={emailChoice === 'existing' ? 'primary' : 'secondary'}
                              onClick={() => setEmailChoice('existing')}
                            >
                              Oui, c'est correct
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEmailChoice('custom')
                                setCustomEmail('')
                              }}
                            >
                              Non, corriger
                            </Button>
                          </div>
                        </div>
                      )}

                      {(emailChoice === 'custom' || !selectedProspect.email) && (
                        <div className="space-y-1">
                          <Input
                            label="Adresse email du client"
                            type="email"
                            value={customEmail}
                            onChange={(e) => {
                              setCustomEmail(e.target.value)
                              setEmailChoice('custom')
                            }}
                            placeholder="client@exemple.fr"
                            leftIcon={<Mail className="w-4 h-4" />}
                          />
                          {selectedProspect.email && (
                            <button
                              type="button"
                              onClick={() => {
                                setEmailChoice('existing')
                                setCustomEmail('')
                              }}
                              className="text-xs text-mint-600 hover:text-mint-700"
                            >
                              Utiliser l'email en base ({selectedProspect.email})
                            </button>
                          )}
                        </div>
                      )}

                      {isEmailValid && (
                        <p className="text-xs text-mint-700">
                          Une confirmation sera envoyée à {effectiveEmail} et un rappel
                          automatique partira la veille du RDV.
                        </p>
                      )}
                      {!isEmailValid && (
                        <p className="text-xs text-red-600">
                          Une adresse email valide est requise pour enregistrer un appel
                          avec un RDV.
                        </p>
                      )}
                    </div>
                  )}

                  <div className="flex justify-end">
                    <Button
                      onClick={handleSaveCall}
                      isLoading={createCall.isPending}
                      disabled={(!callScript && !callNotes && !outcome) || emailBlocksSave}
                      leftIcon={<Save className="w-4 h-4" />}
                    >
                      Enregistrer l'appel
                    </Button>
                  </div>
                </div>
              </Card>

              {/* Call history (collapsible) */}
              <Card padding="sm">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="w-full flex items-center justify-between text-left"
                >
                  <h3 className="text-sm font-semibold text-brown-900 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-mint-600" />
                    Historique des appels
                    {calls && calls.length > 0 && (
                      <span className="text-xs font-normal text-brown-400">({calls.length})</span>
                    )}
                  </h3>
                  {showHistory ? (
                    <ChevronUp className="w-4 h-4 text-brown-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-brown-400" />
                  )}
                </button>

                {showHistory && (
                  <div className="mt-3">
                    {callsLoading ? (
                      <div className="flex justify-center py-6">
                        <Spinner size="sm" />
                      </div>
                    ) : !calls || calls.length === 0 ? (
                      <div className="text-center py-6">
                        <PhoneOff className="w-8 h-8 text-brown-300 mx-auto mb-2" />
                        <p className="text-sm text-brown-400">Aucun appel enregistre</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {calls.map((call) => (
                          <div
                            key={call.id}
                            className="p-3 bg-beige-50/50 rounded-lg border border-beige-100"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs font-medium text-brown-700">
                                  {format(new Date(call.call_date), "d MMM yyyy 'a' HH:mm", { locale: fr })}
                                </span>
                                {call.duration_minutes && (
                                  <span className="text-xs text-brown-400 flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {call.duration_minutes} min
                                  </span>
                                )}
                                {call.outcome && (
                                  <Badge variant={CALL_OUTCOME_COLORS[call.outcome]} size="sm">
                                    {CALL_OUTCOME_LABELS[call.outcome]}
                                  </Badge>
                                )}
                                {call.next_appointment && (
                                  <Badge variant="gold" size="sm">
                                    <Calendar className="w-3 h-3 mr-1" />
                                    RDV: {format(new Date(call.next_appointment), 'd MMM HH:mm', { locale: fr })}
                                  </Badge>
                                )}
                              </div>
                              <button
                                onClick={() => handleDeleteCall(call.id)}
                                className="p-1 text-brown-300 hover:text-red-500 transition-colors"
                                title="Supprimer"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>

                            {call.call_script && (
                              <div className="mt-2">
                                <p className="text-xs font-medium text-brown-500 mb-0.5">Script :</p>
                                <p className="text-sm text-brown-700 whitespace-pre-wrap bg-white p-2 rounded border border-beige-100">
                                  {call.call_script}
                                </p>
                              </div>
                            )}

                            {call.notes && (
                              <div className="mt-2">
                                <p className="text-xs font-medium text-brown-500 mb-0.5">Compte-rendu :</p>
                                <p className="text-sm text-brown-700 whitespace-pre-wrap">{call.notes}</p>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
